import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  AlertCircle, 
  CheckCircle, 
  Camera, 
  Send,
  Trash2,
  AlertTriangle,
  Bug
} from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/Layout';
import './ReportBin.css';
import apiClient from '../../services/api';

const ReportBin = () => {
  const { binId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [binData, setBinData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [issueType, setIssueType] = useState('');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState([]);
  const [photoPreview, setPhotoPreview] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [showBinInfo, setShowBinInfo] = useState(false);
  
  // Camera state
  const [cameraOpen, setCameraOpen] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const issueTypes = [
    { value: 'overflow', label: 'Bin Overflow', icon: <Trash2 size={20} />, color: '#e74c3c' },
    { value: 'damaged', label: 'Damaged Bin', icon: <AlertTriangle size={20} />, color: '#f39c12' },
    { value: 'foul_smell', label: 'Foul Smell', icon: <Bug size={20} />, color: '#27ae60' },
    { value: 'pest_infestation', label: 'Pest Infestation', icon: <Bug size={20} />, color: '#c0392b' },
    { value: 'improper_waste', label: 'Improper Waste', icon: <AlertTriangle size={20} />, color: '#f1c40f' },
    { value: 'other', label: 'Other Issue', icon: <AlertCircle size={20} />, color: '#95a5a6' }
  ];

  useEffect(() => {
    const fetchBinData = async () => {
      if (!binId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const response = await apiClient.get(`/bins/${binId}`);
        
        // Handle the standardized response format
        if (response.data.success && response.data.binData) {
          setBinData(response.data.binData);
        } else {
          throw new Error('Invalid bin data received');
        }
      } catch (err) {
        console.error('Error fetching bin data:', err);
        setError(err.response?.data?.message || 'Failed to load bin data. The bin may not exist or you may not have permission to view it.');
        
        // If it's a 404, show a more specific message
        if (err.response?.status === 404) {
          setError('Bin not found. The QR code might be invalid or the bin has been removed.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBinData();
    getUserLocation();
  }, [binId]);

  const getUserLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setLocationError(null);
      },
      (error) => {
        console.error('Error getting location:', error);
        setLocationError('Unable to get your location. Please enable location services.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const handleCameraOpen = () => {
    setCameraOpen(true);
    navigator.mediaDevices.getUserMedia({ 
      video: { 
        facingMode: 'environment',
        width: { ideal: 1280 },
        height: { ideal: 720 }
      } 
    })
      .then(stream => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      })
      .catch(() => {
        navigator.mediaDevices.getUserMedia({ video: true })
          .then(stream => {
            if (videoRef.current) {
              videoRef.current.srcObject = stream;
            }
          })
          .catch(err => {
            console.error("Error accessing camera: ", err);
            toast.error('Could not access camera. Please ensure you have granted permission.');
            setCameraOpen(false);
          });
      });
  };

  const handleCameraClose = () => {
    setCameraOpen(false);
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
    }
  };

  const handleCapture = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d');
      canvasRef.current.width = videoRef.current.videoWidth;
      canvasRef.current.height = videoRef.current.videoHeight;
      context.drawImage(videoRef.current, 0, 0, videoRef.current.videoWidth, videoRef.current.videoHeight);
      
      canvasRef.current.toBlob(blob => {
        const capturedFile = new File([blob], `report-photo-${Date.now()}.jpg`, { type: "image/jpeg" });
        const newPhotos = [...photos, capturedFile];
        const newPreviews = [...photoPreview, URL.createObjectURL(capturedFile)];
        
        setPhotos(newPhotos);
        setPhotoPreview(newPreviews);
        handleCameraClose();
        toast.success('Photo captured successfully!');
      }, 'image/jpeg', 0.8);
    }
  };

  const removePhoto = (index) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    const newPreviews = photoPreview.filter((_, i) => i !== index);
    setPhotos(newPhotos);
    setPhotoPreview(newPreviews);
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!issueType || !description.trim()) {
    toast.error('Please select an issue type and provide a description');
    return;
  }

  if (!userLocation) {
    toast.error('Location is required to submit a report');
    getUserLocation();
    return;
  }

  if (!photos.length) {
    toast.error('Please capture at least one photo of the bin issue');
    return;
  }

  setSubmitting(true);

  try {
    const formData = new FormData();
    formData.append('binId', binId);
    formData.append('issueType', issueType);
    formData.append('description', description.trim());
    formData.append('reporterLatitude', userLocation.latitude);
    formData.append('reporterLongitude', userLocation.longitude);

    // Add photos
    photos.forEach((photo) => {
      formData.append('photos', photo);
    });

    const response = await apiClient.post('/reports', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    if (response.data.isDuplicate) {
      const reportCount = response.data.report?.reportCount || 1;
      toast.success(`Bin already reported. Priority increased with ${reportCount} report${reportCount > 1 ? 's' : ''}.`);
    } else {
      toast.success('Report submitted successfully!');
    }
    
    // Navigate to success page or back to scanner
    navigate('/reports/success', { 
      state: { 
        reportId: response.data.report.reportId,
        binId: binId,
        issueType: issueType,
        isDuplicate: response.data.isDuplicate,
        reportCount: response.data.report?.reportCount || 1,
        priority: response.data.report?.priority
      } 
    });

  } catch (err) {
    console.error('Error submitting report:', err);
    if (err.response?.data?.message) {
      const errorMessage = err.response.data.message;
      
      // Handle distance validation error specifically
      if (errorMessage.includes('within 50 meters')) {
        const distanceMatch = errorMessage.match(/Current distance: ([\d.]+)m/);
        const distance = distanceMatch ? distanceMatch[1] : 'unknown';
        
        toast.error(
          <div className="distance-error-toast">
            <div className="toast-header">
              <AlertTriangle size={18} style={{ marginRight: '8px' }} />
              <strong>Too Far From Bin</strong>
            </div>
            <div className="toast-body">
              <p>You need to be within 50 meters of the bin to submit a report.</p>
              {distance !== 'unknown' && (
                <p>Your current distance: <strong>{distance} meters</strong></p>
              )}
              <p>Please move closer to the bin and try again.</p>
            </div>
          </div>,
          { 
            duration: 10000, // Show for 10 seconds
            style: { maxWidth: '400px' }
          }
        );
      } 
      // Handle photo validation error
      else if (errorMessage.includes('At least one photo is required')) {
        toast.error('Please capture at least one photo of the issue');
      }
      // Handle other error messages
      else {
        toast.error(errorMessage);
      }
    } else {
      console.error('Full error object:', err);
      toast.error('Failed to submit report. Please check your connection and try again.');
    }
  } finally {
    setSubmitting(false);
  }
};

  if (loading) {
    return (
      <Layout>
        <div className="report-bin-loading">
          <div className="loading-spinner"></div>
          <p>Loading report form...</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="report-bin-error">
          <AlertCircle size={48} className="error-icon" />
          <h2>Report an Issue</h2>
          <p className="error-message">{error}</p>
          <button 
            onClick={() => navigate('/')} 
            className="btn btn-primary"
          >
            Back to Home
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="report-bin-container">
        <div className="report-bin-header">
          <h1>Report an Issue</h1>
          <p>Help us keep our bins in good condition by reporting any issues you find.</p>
          
          {isAdmin && (
            <div className="admin-bin-toggle">
              <button 
                onClick={() => setShowBinInfo(!showBinInfo)}
                className="btn btn-sm btn-outline"
              >
                {showBinInfo ? 'Hide' : 'Show'} Bin Information
              </button>
            </div>
          )}
        </div>
        
        {isAdmin && showBinInfo && binData && (
          <div className="bin-info-card admin-only">
            <h3>Bin Information (Admin Only)</h3>
            <div className="bin-details">
              <p><strong>Bin ID:</strong> {binData.binId || 'N/A'}</p>
              <p><strong>Location:</strong> {binData.location?.address || 'Location not specified'}</p>
              <p><strong>Status:</strong> {binData.status || 'Unknown'}</p>
              {binData.lastEmptied && (
                <p><strong>Last Emptied:</strong> {new Date(binData.lastEmptied).toLocaleDateString()}</p>
              )}
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="report-form">
          <div className="form-section">
            <h3>What's the issue?</h3>
            <div className="issue-type-grid">
              {issueTypes.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  className={`issue-type-btn ${issueType === type.value ? 'active' : ''}`}
                  onClick={() => setIssueType(type.value)}
                  style={{ borderColor: type.color }}
                >
                  <span className="issue-icon" style={{ color: type.color }}>
                    {type.icon}
                  </span>
                  {type.label}
                </button>
              ))}
            </div>
          </div>
          
          <div className="form-section">
            <h3>Description</h3>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please describe the issue in detail..."
              rows={4}
              required
            />
          </div>
          
          <div className="form-section">
            <h3>Add Photos (Required)</h3>
            <div className="photo-upload">
              <button
                type="button"
                onClick={handleCameraOpen}
                className="btn btn-outline"
                disabled={photos.length >= 5}
              >
                <Camera size={16} /> {photos.length}/5 Photos
              </button>
              <p className="help-text">Capture at least one clear photo. Maximum 5 photos, 5MB each.</p>

              <div className="auto-priority-note">
                <AlertCircle size={16} />
                <span>Priority increases automatically as more users report the same bin.</span>
              </div>
              
              <div className="photo-preview">
                {photoPreview.map((preview, index) => (
                  <div key={index} className="photo-thumbnail">
                    <img src={preview} alt={`Preview ${index + 1}`} />
                    <button
                      type="button"
                      className="remove-photo"
                      onClick={() => removePhoto(index)}
                      aria-label="Remove photo"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h3>Your Location</h3>
            {locationError ? (
              <div className="location-error">
                <AlertCircle size={16} />
                <span>{locationError}</span>
                <button
                  type="button"
                  onClick={getUserLocation}
                  className="btn btn-sm btn-outline"
                >
                  Try Again
                </button>
              </div>
            ) : userLocation ? (
              <div className="location-confirmation">
                <CheckCircle size={16} className="success-icon" />
                <span>Location captured: {userLocation.latitude.toFixed(6)}, {userLocation.longitude.toFixed(6)}</span>
              </div>
            ) : (
              <div className="location-loading">
                <div className="spinner"></div>
                <span>Getting your location...</span>
              </div>
            )}
          </div>
          
          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || !userLocation}
            >
              {submitting ? (
                <>
                  <span className="spinner"></span>
                  Submitting...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Submit Report
                </>
              )}
            </button>
            
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="btn btn-text"
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
        </form>

        {/* Camera Modal */}
        {cameraOpen && (
          <div className="camera-modal">
            <div className="camera-modal-content">
              <div className="camera-header">
                <h3>Capture Photo</h3>
                <button onClick={handleCameraClose} className="close-btn">×</button>
              </div>
              <div className="camera-body">
                <video 
                  ref={videoRef} 
                  autoPlay 
                  playsInline 
                  className="camera-video"
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </div>
              <div className="camera-actions">
                <button onClick={handleCapture} className="capture-btn">
                  <Camera size={20} />
                  Capture
                </button>
                <button onClick={handleCameraClose} className="cancel-btn">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ReportBin;
